import { Rgb, Scene, WallpaperStyleInfo, darken, degrees, lighten, luminance, mix, prng, rgba, share } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'circuit', label: 'Circuit',
  blurb: 'Traces and solder pads winding across a circuit board',
  params: [ share( 'density', 'Density', 50 ), degrees( 'angle', 'Angle', 0 ) ],
};

// the eight routing directions 45 degrees apart, so a turn is one index step (45) or two (90)
const DIRS = [ [ 1, 0 ], [ 1, 1 ], [ 0, 1 ], [ -1, 1 ], [ -1, 0 ], [ -1, -1 ], [ 0, -1 ], [ 1, -1 ] ];

// what a node of the lattice holds
const FREE = 0, TRACE = 1, CHIP = 2, PAD = 3;

interface Chip { x: number; y: number; dir: number; }
interface Walk { nodes: number[]; color: Rgb; chip?: Chip; }

/**
 * Circuit: seeded random walks on a square lattice, routed the way a PCB is (straight runs, turns
 * of 45 or 90 only, never through an occupied node), each stroked as one round path with a solder
 * pad at both ends and one in eight ending in a chip. The first tile is the board, the traces walk
 * the rest in runs of three per tile so the colours come in patches rather than confetti.
 */
export function draw( { ctx, w, h, box, colors, rand, p, seed }: Scene ): void {
  const rawTraces = colors.length > 1 ? colors.slice( 1 ) : [ lighten( colors[ 0 ], 0.6 ) ];
  const board = boardFor( colors[ 0 ], rawTraces );
  const boardLum = luminance( board );
  const traces = rawTraces.map( c => readable( c, boardLum ) );
  const dark = boardLum < 0.35;
  ctx.fillStyle = rgba( board, 1 );
  ctx.fillRect( 0, 0, w, h );

  // Density sets the columns across the box; the lattice is square and covers the box's diagonal
  // with a margin, so any Angle still fills the canvas and every size draws the same board
  const cols = 30 + Math.round( p[ 0 ] * 60 );
  const cell = box.w / cols;
  // the lattice covers the CANVAS diagonal, not the box's, so an ultrawide board is traced edge to
  // edge instead of leaving the sides bare (owner, 2026-09-26)
  const n = Math.ceil( Math.hypot( w, h ) / cell ) + 4;
  // the lattice is indexed from its centre, so a cell keeps its place (and its own generator)
  // whatever the canvas is: an ultrawide then draws the SAME board with more of it at the sides
  const base = Math.floor( n / 2 );
  const occ = new Uint8Array( n * n );
  const inside = ( x: number, y: number ) => x >= 0 && y >= 0 && x < n && y < n;
  const mark = ( x: number, y: number ) => occ[ y * n + x ];
  // a diagonal step may not cut between two occupied orthogonal neighbours: two traces would cross
  const canStep = ( x: number, y: number, d: number ) => {
    const dx = DIRS[ d ][ 0 ], dy = DIRS[ d ][ 1 ];
    const nx = x + dx, ny = y + dy;
    if ( !inside( nx, ny ) || mark( nx, ny ) !== FREE ) return false;
    return !( dx && dy && mark( nx, y ) !== FREE && mark( x, ny ) !== FREE );
  };
  // pads and chips stay clear of each other: an end node with one next to it retreats a step
  const nearFixture = ( x: number, y: number ) => {
    for ( let j = -1; j <= 1; j++ ) for ( let i = -1; i <= 1; i++ ) {
      if ( ( i || j ) && inside( x + i, y + j ) && mark( x + i, y + j ) >= CHIP ) return true;
    }
    return false;
  };
  // diagonal runs are short jogs, orthogonal runs are the long straights of a real board
  const runLength = ( g: () => number, d: number ) => d % 2 ? 1 + Math.floor( g() * 4 ) : 3 + Math.floor( g() * 8 );
  const turnOptions = ( g: () => number, d: number ) => {
    const s = g() < 0.5 ? 1 : -1;
    return g() < 0.3 ? [ d + 2 * s, d - 2 * s, d + s, d - s ] : [ d + s, d - s, d + 2 * s, d - 2 * s ];
  };
  // the chip's node block: three along the direction of travel by three across (the body is
  // two by three cells between them), plus a clear ring so no pad or other chip touches it
  const chipFits = ( x: number, y: number, d: number ) => {
    const ax = DIRS[ d ][ 0 ], ay = DIRS[ d ][ 1 ], bx = -ay, by = ax;
    for ( let a = 0; a <= 4; a++ ) for ( let b = -2; b <= 2; b++ ) {
      const cx = x + ax * a + bx * b, cy = y + ay * a + by * b;
      if ( !inside( cx, cy ) ) return false;
      const m = mark( cx, cy );
      const core = a >= 1 && a <= 3 && b >= -1 && b <= 1;
      if ( core ? m !== FREE : m >= CHIP ) return false;
    }
    return true;
  };
  const placeChip = ( x: number, y: number, d: number ) => {
    const ax = DIRS[ d ][ 0 ], ay = DIRS[ d ][ 1 ], bx = -ay, by = ax;
    for ( let a = 1; a <= 3; a++ ) for ( let b = -1; b <= 1; b++ ) occ[ ( y + ay * a + by * b ) * n + x + ax * a + bx * b ] = CHIP;
  };

  const walk = ( g: () => number, sx: number, sy: number, d: number, wantChip: boolean ): Walk | null => {
    if ( mark( sx, sy ) !== FREE || nearFixture( sx, sy ) ) return null;
    const nodes = [ sx, sy ];
    occ[ sy * n + sx ] = TRACE;
    const maxSteps = 8 + Math.floor( g() * 33 );
    let x = sx, y = sy, run = runLength( g, d ), chip: Chip | undefined;
    for ( let step = 0; step < maxSteps; step++ ) {
      // a chip walk ends in its chip as soon as it is straight, has some length and the block is free
      if ( wantChip && step >= 5 && d % 2 === 0 && chipFits( x, y, d ) ) {
        placeChip( x, y, d );
        chip = { x, y, dir: d };
        break;
      }
      if ( run <= 0 || !canStep( x, y, d ) ) {
        const next = turnOptions( g, d ).map( o => ( o + 8 ) % 8 ).find( o => canStep( x, y, o ) );
        if ( next !== undefined ) {
          d = next;
          run = runLength( g, d );
        } else if ( canStep( x, y, d ) ) {
          run = runLength( g, d );
        } else {
          break;
        }
      }
      x += DIRS[ d ][ 0 ];
      y += DIRS[ d ][ 1 ];
      occ[ y * n + x ] = TRACE;
      nodes.push( x, y );
      run--;
    }
    // a stub is no trace: give its nodes back
    if ( nodes.length < 6 && !chip ) {
      for ( let i = 0; i < nodes.length; i += 2 ) occ[ nodes[ i + 1 ] * n + nodes[ i ] ] = FREE;
      return null;
    }
    if ( !chip && nearFixture( x, y ) && nodes.length >= 8 ) {
      occ[ y * n + x ] = FREE;
      nodes.length -= 2;
    }
    occ[ nodes[ 1 ] * n + nodes[ 0 ] ] = PAD;
    if ( !chip ) occ[ nodes[ nodes.length - 1 ] * n + nodes[ nodes.length - 2 ] ] = PAD;
    return { nodes, color: traces[ 0 ], chip };
  };

  // the start nodes: a coarse lattice jittered by up to half its spacing, listed row by row so the
  // colour runs land beside each other; Density fills the board as well as it thins the grid, so
  // 0 is a few thick traces with room around them and 100 a board packed with routing
  const spacing = 7 - Math.round( p[ 0 ] * 3 );
  const jitter = Math.floor( spacing / 2 );
  const starts: { x: number; y: number; dir: number; chip: boolean; g: () => number; ax: number; ay: number }[] = [];
  const reach = Math.ceil( base / spacing ) * spacing;
  for ( let ay = -reach; ay <= reach; ay += spacing ) {
    for ( let ax = -reach; ax <= reach; ax += spacing ) {
      // the cell's own generator, from its place on the board and the seed
      const g = prng( ( Math.imul( seed, 2654435761 ) + ax * 1000003 + ay * 7919 ) >>> 0 );
      const x = base + ax + Math.floor( g() * ( jitter * 2 + 1 ) ) - jitter;
      const y = base + ay + Math.floor( g() * ( jitter * 2 + 1 ) ) - jitter;
      // most traces set off along the board's axes, a few on the diagonal
      const dir = g() < 0.75 ? Math.floor( g() * 4 ) * 2 : Math.floor( g() * 4 ) * 2 + 1;
      // every eighth cell of a row carries a chip, counted from the centre so it does not move
      const chip = ( ( Math.round( ax / spacing ) + Math.round( ay / spacing ) * 3 ) % 8 + 8 ) % 8 === 7;
      starts.push( { x, y, dir, chip, g, ax, ay } );
    }
  }
  // the chip walks go first, while the board is still empty: a chip needs a clear block, so routed
  // last they all ended up in the rows walked first
  const walks: Walk[] = [];
  for ( const pass of [ true, false ] ) {
    starts.forEach( ( s, i ) => {
      if ( s.chip !== pass ) return;
      const wk = inside( s.x, s.y ) ? walk( s.g, s.x, s.y, s.dir, s.chip ) : null;
      if ( !wk ) return;
      // the colour follows the node's place on the board, not the order it was routed in, and that
      // place is counted from the centre, so a wider canvas paints the middle the same
      const run = Math.floor( s.ax / spacing / 3 ) + Math.round( s.ay / spacing );
      wk.color = traces[ ( run % traces.length + traces.length ) % traces.length ];
      walks.push( wk );
    } );
  }

  // everything below is drawn in the board's frame: the lattice centred on the box, turned by Angle
  ctx.save();
  ctx.translate( box.x + box.w / 2, box.y + box.h / 2 );
  ctx.rotate( p[ 1 ] * Math.PI * 2 );
  const px = ( gx: number ) => ( gx - base ) * cell;
  const trace = ( wk: Walk ) => {
    const m = wk.nodes;
    ctx.beginPath();
    ctx.moveTo( px( m[ 0 ] ), px( m[ 1 ] ) );
    for ( let i = 2; i < m.length; i += 2 ) ctx.lineTo( px( m[ i ] ), px( m[ i + 1 ] ) );
    // a chip walk runs on half a cell into the chip's edge
    if ( wk.chip ) ctx.lineTo( px( wk.chip.x + DIRS[ wk.chip.dir ][ 0 ] * 0.5 ), px( wk.chip.y + DIRS[ wk.chip.dir ][ 1 ] * 0.5 ) );
    ctx.stroke();
  };
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // the glow: the same paths, wide and faint, only on a dark board and only this faint, so it
  // reads as a lit trace and never as glass
  if ( dark ) {
    ctx.lineWidth = cell * 0.7;
    for ( const wk of walks ) {
      ctx.strokeStyle = rgba( wk.color, 0.12 );
      trace( wk );
    }
  }
  ctx.lineWidth = cell * 0.28;
  for ( const wk of walks ) {
    ctx.strokeStyle = rgba( wk.color, 1 );
    trace( wk );
  }
  // the pads: a disc with the through hole showing the board
  const pad = ( gx: number, gy: number, c: Rgb ) => {
    ctx.fillStyle = rgba( c, 1 );
    ctx.beginPath();
    ctx.arc( px( gx ), px( gy ), cell * 0.45, 0, Math.PI * 2 );
    ctx.fill();
    ctx.fillStyle = rgba( board, 1 );
    ctx.beginPath();
    ctx.arc( px( gx ), px( gy ), cell * 0.18, 0, Math.PI * 2 );
    ctx.fill();
  };
  for ( const wk of walks ) {
    const m = wk.nodes;
    pad( m[ 0 ], m[ 1 ], wk.color );
    if ( !wk.chip ) pad( m[ m.length - 2 ], m[ m.length - 1 ], wk.color );
  }
  // the chips: a rounded body two by three cells with three pins a side and a pin one dimple
  const shell = dark ? lighten( board, 0.2 ) : darken( board, 0.5 );
  for ( const wk of walks ) {
    if ( !wk.chip ) continue;
    const { x, y, dir } = wk.chip;
    // a touch of its trace in the body, so the chip belongs to the palette instead of sitting on it as grey
    const body = mix( shell, wk.color, 0.14 );
    ctx.save();
    ctx.translate( px( x + DIRS[ dir ][ 0 ] * 2 ), px( y + DIRS[ dir ][ 1 ] * 2 ) );
    ctx.rotate( dir * Math.PI / 4 );
    ctx.strokeStyle = rgba( wk.color, 1 );
    ctx.lineWidth = cell * 0.22;
    ctx.beginPath();
    for ( let i = -1; i <= 1; i++ ) {
      ctx.moveTo( i * cell, -cell * 0.9 );
      ctx.lineTo( i * cell, -cell * 1.4 );
      ctx.moveTo( i * cell, cell * 0.9 );
      ctx.lineTo( i * cell, cell * 1.4 );
    }
    ctx.stroke();
    ctx.fillStyle = rgba( body, 1 );
    roundRect( ctx, -cell * 1.5, -cell, cell * 3, cell * 2, cell * 0.2 );
    ctx.fill();
    ctx.strokeStyle = rgba( mix( body, wk.color, 0.5 ), 1 );
    ctx.lineWidth = cell * 0.06;
    ctx.stroke();
    ctx.fillStyle = rgba( mix( body, board, 0.6 ), 1 );
    ctx.beginPath();
    ctx.arc( -cell * 1.05, -cell * 0.55, cell * 0.14, 0, Math.PI * 2 );
    ctx.fill();
    ctx.restore();
  }
  ctx.restore();
}

/**
 * The board keeps the first tile unless the traces would not read on it (a ramp palette starts a
 * shade away from its base): the least shift that clearly lifts the average contrast wins.
 */
function boardFor( base: Rgb, traces: Rgb[] ): Rgb {
  const score = ( b: Rgb ) => {
    const l = luminance( b );
    let s = 0;
    for ( const t of traces ) s += Math.min( 0.3, Math.abs( luminance( t ) - l ) );
    return s / traces.length;
  };
  let best = base, bestScore = score( base ) + 0.04;
  for ( const t of [ 0.2, 0.4, 0.6 ] ) {
    for ( const c of [ darken( base, t ), lighten( base, t ) ] ) {
      const s = score( c );
      if ( s > bestScore + 0.01 ) {
        best = c;
        bestScore = s;
      }
    }
  }
  return best;
}

/** a trace a shade away from the board is pushed off it, away from the board's own side, until it reads */
function readable( c: Rgb, boardLum: number ): Rgb {
  const gap = 0.22;
  let out = c;
  for ( let i = 0; i < 12 && Math.abs( luminance( out ) - boardLum ) < gap; i++ ) {
    out = boardLum >= 0.5 ? darken( out, 0.1 ) : lighten( out, 0.1 );
  }
  return out;
}

function roundRect( ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number ): void {
  ctx.beginPath();
  ctx.moveTo( x + r, y );
  ctx.lineTo( x + w - r, y );
  ctx.arcTo( x + w, y, x + w, y + r, r );
  ctx.lineTo( x + w, y + h - r );
  ctx.arcTo( x + w, y + h, x + w - r, y + h, r );
  ctx.lineTo( x + r, y + h );
  ctx.arcTo( x, y + h, x, y + h - r, r );
  ctx.lineTo( x, y + r );
  ctx.arcTo( x, y, x + r, y, r );
  ctx.closePath();
}
